import { ArrowRight, Circle } from 'lucide-react'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { SsoAlert } from '@/features/auth/components/SsoAlert'
import { SsoButtons } from '@/features/auth/components/SsoButtons'
import { ssoErrorTone } from '@/features/auth/lib/ssoErrors'
import { ssoInviteStash } from '@/features/auth/lib/ssoInviteStash'
import { useSsoErrorParam } from '@/features/auth/lib/useSsoErrorParam'
import { loadSelfRegistrationEnabled } from '@/features/auth/services/authService.http'
import { signIn } from '@/features/auth/slices/sessionSlice'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * `/login` (PX-W6.1 rewrite) — the REAL front door: email + password against
 * `POST /api/v1/auth/login`. The demo persona picker is gone from this route
 * (AC-4); the demo layer itself stays importable elsewhere until W6.6.
 *
 * This is the ONE surface outside `AppShell` beside `/oauth/consent` and
 * `/onboarding/01`: no top bar, no rail, no context pill, no chip.
 *
 * Credentials (I-1 / I-17, amended): the password lives in one local
 * `useState`, goes to `POST /api/v1/auth/login` ONLY (over the dev proxy in
 * dev), is never written to the store, never written to web storage, and is
 * cleared the moment the submit fires. The access token that comes back lives
 * in the auth service's module memory; the refresh token is an httpOnly
 * cookie the browser manages.
 *
 * On success the page routes to `/` and `RoleLanding` renders the derived
 * role's landing (the lens bridge keeps the shell's rail honest until W6.2+).
 *
 * SSO (CR-SSO-INVITE-PX-2026-09-26) is LIVE when configured: `SsoButtons`
 * renders "Continue with Google / Microsoft" for exactly the providers the
 * environment lists, and nothing at all otherwise — no divider, no dead
 * button, no concept badge. A failed SSO round trip comes back as
 * `?ssoError=<key>`, rendered from an allowlist in the session-expired slot at
 * the top of the card. Below `lg` the page stacks to one column (Agon's ruling,
 * 2026-09-26): the brand column keeps its mark and promise and drops the long
 * copy, so the card is reachable at 390 px.
 */
export default function LoginPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation(['auth', 'common', 'errors'])
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Invite-only environments hide the sign-up link; hidden until the server says open.
  const [signUpOpen, setSignUpOpen] = useState(false)
  useEffect(() => {
    let live = true
    void loadSelfRegistrationEnabled().then((open) => {
      if (live) setSignUpOpen(open)
    })
    return () => {
      live = false
    }
  }, [])
  const sessionExpired = searchParams.get('reason') === 'session-expired'
  // Seeded once from the URL; the hook strips the param so a reload does not
  // show a stale SSO message again.
  const initialSsoError = useSsoErrorParam()
  const [ssoError, setSsoError] = useState<string | null>(initialSsoError)
  // Two separate statements on purpose: `submitting || useAppSelector(...)` would
  // SHORT-CIRCUIT the hook away once submitting is true and corrupt React's
  // hook order. Hooks are never conditional, and `||` is.
  const sessionAuthenticating = useAppSelector((s) => s.session.status === 'authenticating')
  const busy = submitting || sessionAuthenticating

  const onEmailChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    setErrorKey(null)
  }, [])

  const onPasswordChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
    setErrorKey(null)
  }, [])

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (email.trim().length === 0 || password.length === 0) {
        setErrorKey('auth.credentials.invalid')
        return
      }
      // The password has done its job; drop it before anything renders again.
      setPassword('')
      setSsoError(null)
      setSubmitting(true)
      try {
        await dispatch(
          signIn({ email: email.trim(), password })
        ).unwrap()
        // Arrived from "sign in to accept" (TASK-714FBFBD fix cycle 1b, INV-S9):
        // back to the invite, which now accepts for this session.
        const invite = ssoInviteStash.take()
        navigate(invite === null ? '/' : `/accept-invite?token=${encodeURIComponent(invite)}`)
      } catch (rejected) {
        const backendKey =
          typeof rejected === 'object' && rejected !== null && 'backendKey' in rejected
            ? (rejected as { backendKey?: string }).backendKey
            : undefined
        setErrorKey(backendKey ?? 'generic')
      } finally {
        setSubmitting(false)
      }
    },
    [email, password, dispatch, navigate]
  )

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <div className="mx-auto flex w-full max-w-[72rem] flex-1 flex-col justify-center px-6 py-10">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2">
          <div className="flex flex-col pt-2 lg:sticky lg:top-10">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary font-serif text-base font-bold text-primary-foreground">
                {t('auth:brand.mark')}
              </span>
              <span className="text-sm font-medium text-ink-soft">{t('auth:brand.wordmark')}</span>
            </div>
            <h1 className="mt-6 max-w-lg font-serif text-3xl font-semibold leading-tight text-foreground">
              {t('auth:brand.promise')}
            </h1>
            <p className="mt-3 hidden max-w-lg text-sm text-muted-foreground lg:block">
              {t('auth:brand.standfirst')}
            </p>
            <dl className="mt-6 hidden max-w-lg gap-3 lg:grid">
              {(
                t('auth:values', { returnObjects: true }) as unknown as {
                  title: string
                  detail: string
                }[]
              ).map(
                (value) => (
                  <div key={value.title} className="flex items-start gap-2">
                    <span
                      aria-hidden
                      className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"
                    >
                      <Circle className="h-2 w-2 fill-current" />
                    </span>
                    <div>
                      <dt className="text-sm font-medium text-foreground">{value.title}</dt>
                      <dd className="text-xs text-muted-foreground">{value.detail}</dd>
                    </div>
                  </div>
                )
              )}
            </dl>
            <p className="mt-8 hidden max-w-lg text-xs text-muted-foreground lg:block">
              {t('auth:brand.doorLine')}
            </p>
          </div>

          <div className="min-w-0 rounded-lg border border-border bg-card px-6 py-6">
            <h2 className="font-serif text-xl font-semibold text-foreground">
              {t('auth:form.heading')}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('auth:form.lede')}</p>

            {sessionExpired && (
              <p
                role="status"
                className="mt-3 rounded-md border border-border bg-muted p-2 text-xs text-ink-soft"
              >
                {t('errors:session-expired')}
              </p>
            )}

            {ssoError !== null && (
              <div className="mt-3">
                <SsoAlert tone={ssoErrorTone(ssoError)} messageKey={`errors:${ssoError}`}>
                  {t(`errors:${ssoError}`)}
                </SsoAlert>
              </div>
            )}

            <form onSubmit={onSubmit} noValidate>
              <div className="mt-4 grid gap-3">
                <label className="block">
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {t('auth:form.emailLabel')}
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={onEmailChanged}
                    autoComplete="username"
                    aria-label={t('auth:form.emailLabel')}
                    className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
                  />
                </label>
                <label className="block">
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {t('auth:form.passwordLabel')}
                  </span>
                  <input
                    type="password"
                    value={password}
                    onChange={onPasswordChanged}
                    autoComplete="current-password"
                    aria-label={t('auth:form.passwordLabel')}
                    className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
                  />
                </label>
              </div>

              {errorKey && (
                <p role="alert" className="mt-2 text-xs font-medium text-blocked">
                  {t(`errors:${errorKey}`, { defaultValue: t('errors:generic') })}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                aria-busy={busy}
                className="mt-4 inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {busy ? t('auth:form.submitting') : t('auth:form.submit')}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            </form>

            {/* After </form>: Enter in a field always submits the password form. */}
            <SsoButtons mode={{ kind: 'login' }} />

            {signUpOpen && (
              <div className="mt-6 border-t border-border pt-4">
                <p className="text-xs text-muted-foreground">
                  <Link
                    to="/onboarding/01"
                    className="font-medium text-foreground underline underline-offset-4"
                  >
                    {t('auth:form.onboardingLink')}
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>

        <p className="mx-auto mt-10 max-w-3xl text-center text-xs italic text-muted-foreground">
          {t('auth:densityNote')}
        </p>
      </div>
    </div>
  )
}
